import { BodyScrollEvent, GetDataPath, RowClassParams, RowHeightParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable, SlidingPanelContent } from 'core/components';
import { Loader } from 'core/elements';
import { AgGridClientTableProvider } from 'core/states';
import { isPromotedSingleChild } from 'core/utils';
import { CorporatePartNotSetUp } from 'corporate/components/CorporatePartNotSetUp';
import { CorporatePartUpsertButton } from 'corporate/components/CorporatePartSetUp';
import { UpsertCorporatePartCostButton } from 'corporate/components/UpsertCorporatePartCost';
import { CorporatePartCostStatus } from 'corporate/enums';
import { CorporatePartHelper } from 'corporate/helpers';
import { useCanManageParts, useGetCorporatePartCosts } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartCostListPayload } from 'corporate/types';
import { PartCostCards, VendorCostCard } from 'parts/components/PartDetails/Costs/PartCostCards';

import { columnDef } from './columns';
import { flattenCostRecords, IFlatCostRow } from './utils';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  partNumber: string;
  manufacturerId: number;
  enabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  onBodyScroll?: (event: BodyScrollEvent) => void;
}

const LEAF_ROW_HEIGHT = 42;

const autoGroupColumnDef = {
  headerName: '',
  width: 50,
  maxWidth: 50,
  pinned: 'left' as const,
  lockPinned: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
  resizable: false,
  sortable: false,
  filter: false,
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: () => null,
  },
};

export const CorporatePartCostList = ({
  corporatePart,
  partNumber,
  manufacturerId,
  enabled = false,
  fullWidth = true,
  isLoading = false,
  onBodyScroll,
}: IProps) => {
  const { t } = useTranslation();

  const isSetUp = CorporatePartHelper.isSetUp(corporatePart);
  const canManageParts = useCanManageParts();
  const canManagePartCosts = canManageParts && isSetUp;

  const payload: ICorporatePartCostListPayload = {
    filters: {
      corporatePartId: corporatePart?.id,
    },
    pageSize: 100,
  };

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetCorporatePartCosts(payload, { enabled: enabled && isSetUp, fetchAllPages: true });

  const flatRows = useMemo(() => flattenCostRecords(records), [records]);

  const defaultColDef = useMemo(() => ({ autoHeight: false }), []);

  const columnDefs = useMemo(
    () => columnDef({ corporatePart, canManagePartCosts }),
    [corporatePart, canManagePartCosts]
  );

  const getDataPath = useCallback<GetDataPath<IFlatCostRow>>(
    (data) => [String(data.recordId), String(data.itemId ?? data.recordId)],
    []
  );

  const getRowClass = useCallback(({ data, node }: RowClassParams<IFlatCostRow>) => {
    const status = node?.group ? node.allLeafChildren?.[0]?.data?.status : data?.status;

    const classes: string[] = [];

    if (status === CorporatePartCostStatus.EXPIRED || status === CorporatePartCostStatus.OLD) {
      classes.push('disabledRow');
    }

    // Resolve sibling position from the post-sort list so alternation tracks the
    // displayed order, not the source-data order.
    const groupNode = node?.group ? node : node?.parent;

    if (groupNode?.parent) {
      const siblings =
        groupNode.parent.childrenAfterSort ??
        groupNode.parent.childrenAfterFilter ??
        groupNode.parent.childrenAfterGroup;
      const index = siblings ? siblings.indexOf(groupNode) : -1;

      if (index >= 0 && index % 2 === 1) {
        classes.push('oddGroup');
      }
    }

    return classes.length ? classes : undefined;
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams<IFlatCostRow>) => {
    if (params.node?.group) {
      return undefined;
    }
    if (isPromotedSingleChild({ node: params.node })) {
      return undefined;
    }

    return LEAF_ROW_HEIGHT;
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: t('labelColumns'),
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: t('labelFilters'),
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    }),
    [t]
  );

  // Both buttons self-gate on the Manage Parts privilege — they render null
  // when the user lacks it, so the panel-extra slot doesn't need a redundant
  // outer guard. Branch only on the surface state (set-up vs not).
  const slidingPanelExtra = useMemo(() => {
    if (!isSetUp) {
      return (
        <CorporatePartUpsertButton
          corporatePart={corporatePart}
          partNumber={partNumber}
          manufacturerId={manufacturerId}
        />
      );
    }

    return <UpsertCorporatePartCostButton corporatePart={corporatePart} />;
  }, [isSetUp, corporatePart, partNumber, manufacturerId]);

  return (
    <AgGridClientTableProvider
      tableId="corporatePartCostList"
      entityType="CostItem"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={flatRows}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      hasRowSelection={false}
      hasGroupByEnabled={false}
      treeData
      getDataPath={getDataPath}
      groupDefaultExpanded={-1}
      groupRemoveSingleChildren
      autoGroupColumnDef={autoGroupColumnDef}
      getRowClass={getRowClass}
      getRowHeight={getRowHeight}
      sideBar={sideBar}
      onBodyScroll={onBodyScroll}
    >
      <SlidingPanelContent
        title={t('titleCosts')}
        extra={!isLoading && slidingPanelExtra}
        fullWidth={fullWidth}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <PartCostCards
            partNumber={partNumber}
            manufacturerId={manufacturerId}
            firstCard={
              isSetUp ? (
                <VendorCostCard
                  groupName={t('labelCompany')}
                  standardCost={corporatePart?.cost}
                  purchaseCost={corporatePart?.purchaseCost}
                  uom={corporatePart?.pricingUom}
                  highlighted
                />
              ) : null
            }
            enabled={enabled}
          />
        )}
        {!isLoading && isSetUp && <AgGridTable />}
        {!isLoading && !isSetUp && <CorporatePartNotSetUp />}
      </SlidingPanelContent>
    </AgGridClientTableProvider>
  );
};
